import { ExternalTableQueryDto } from "@/lib/apis/hub/models";

import { LINE_COLUMNS, VARIABLE_PARTS_SEPARATOR, VARIABLE_PREFIXES } from "./formulaEditor.constants";
import { getFunctionSuggestions } from "./formulaFunctions";
import { getExternalTableExpectedArgs, unquote } from "./formulaEditor.table";
import {
	ExternalTableExpectedArg,
	Suggestion,
	TableCompletionContext,
	Variable,
	VariableType
} from "./formulaEditor.types";

export interface VariableSuggestionsRequest {
	kind: "initial" | "remote";
	suggestions?: Suggestion[];
	source?: VariableType;
	query?: string;
}

export function getPrefixSuggestions(code: string): Suggestion[] {
	return VARIABLE_PREFIXES
		.filter(prefix => prefix.toLowerCase().includes(code.toLowerCase()))
		.map(prefix => ({
			code: prefix,
			kind: "keyword",
			section: "Préfixes de variable"
		}));
}

export function getRootSuggestions(code: string): Suggestion[] {
	return [...getFunctionSuggestions(code), ...getPrefixSuggestions(code)];
}

export function toVariableSuggestions(items: Array<{ code: string; label?: string }>): Suggestion[] {
	return items.map(item => ({
		code: item.code,
		label: item.label,
		kind: "variable",
		section: "Variables"
	}));
}

export function getLigneColumnSuggestions(variable: Variable): Suggestion[] {
	const endsWithColon = variable.code.endsWith(VARIABLE_PARTS_SEPARATOR);
	const endsWithColumn = LINE_COLUMNS.some(column => variable.code.endsWith(column + VARIABLE_PARTS_SEPARATOR));
	const codeParts = variable.code.split(VARIABLE_PARTS_SEPARATOR);
	const lastPart = codeParts[codeParts.length - 1] ?? "";
	const matchingColumns = LINE_COLUMNS.filter(column => column.toLowerCase().includes(lastPart.toLowerCase()));

	if ((endsWithColon && !endsWithColumn) || (!endsWithColon && matchingColumns.length)) {
		return matchingColumns.map(code => ({
			code,
			append: true,
			appendSeparator: true,
			last: true,
			kind: "field",
			section: "Colonnes de ligne"
		}));
	}

	return [];
}

export function getVariableSuggestionsRequest(editorValue: string, variable: Variable): VariableSuggestionsRequest {
	if (!editorValue || (!variable.code && !variable.prefix)) {
		return {
			kind: "initial",
			suggestions: getRootSuggestions("")
		};
	}

	if (!variable.prefix) {
		return {
			kind: "initial",
			suggestions: getRootSuggestions(variable.code)
		};
	}

	return {
		kind: "remote",
		source: variable.prefix,
		query: variable.code
	};
}

export function getTableQueryCode(context: TableCompletionContext): string {
	return unquote(context.tableContext.args[0] || "");
}

export function getExpectedTableArg(
	context: TableCompletionContext,
	queries: ExternalTableQueryDto[]
): ExternalTableExpectedArg | null {
	const queryCode = getTableQueryCode(context);

	if (!queryCode) {
		return null;
	}

	const selectedQuery = queries.find(query => query.code === queryCode);
	if (!selectedQuery) {
		return null;
	}

	const expectedArgs = getExternalTableExpectedArgs(selectedQuery);
	const expectedArgIndex = context.tableContext.currentArgIndex - 1;

	if (expectedArgIndex < 0 || expectedArgIndex >= expectedArgs.length) {
		return null;
	}

	return expectedArgs[expectedArgIndex];
}